// The accountability log (docs/mastermind-admin-plan.md 4.3 and 4.4): 1 component for the member's page
// (in the product's look) and Adam's tab (in the shell's), over 1 pure model (log.js) and 1 file of
// words (logWords.js). look picks the --log-* variables (styles/mastermind-log.css): 'product' maps them
// to the mastermind's own tokens and 'shell' to admin's, so the looks never share a page and a saved PDF
// is plain black and white.
//
// Marks are a shape and a word, never colour alone. Each month stays together on a printed page where
// it fits and rows never split. tools: what sits above the document on screen (Adam's buttons, the
// member's print button), never printed.
//
// The member's page (look 'product') on a phone, on screen only, so the PDF and Adam's copies stay as they
// are (the Accelerator critique's member 8, 20 Oct; styles/mastermind-log.css): Sessions and Actions as 2
// tiles and Goal and Membership as 1 plain line, this month's card straight after them, above the strip,
// each box of the strip a jump to its month, the months that went cleanly folded to their heading, and Print
// at the bottom in About this log (aboutTools). On every width there, a missed session's "recording not
// watched" links to its recording (recordingHref, member 9).
import { useEffect, useMemo, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { logView } from './log.js'
import { LOG, LOG_MEMBER, LOG_PAGE } from './logWords.js'
import { SITE } from '../../config/site.js'
import '../../styles/mastermind-log.css'

// The page's class for printing while a log is open: the frame, the menu and the tools hidden
// (styles/mastermind-log.css)
export function usePrintClass() {
  useEffect(() => {
    document.documentElement.classList.add('mlog-printing')
    return () => document.documentElement.classList.remove('mlog-printing')
  }, [])
}

// The shapes. Each is drawn in currentColor, 16px, and hidden from screen readers: the word beside it
// carries the meaning.
export function MarkIcon({ mark, size = 16 }) {
  const p = { width: size, height: size, viewBox: '0 0 16 16', 'aria-hidden': true, focusable: 'false', className: `mlog-ico mlog-ico--${mark}` }
  switch (mark) {
    case 'done':
      return <svg {...p}><circle cx="8" cy="8" r="7" fill="currentColor" /><path d="M4.6 8.3l2.2 2.2 4.6-4.8" fill="none" stroke="var(--log-card)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    case 'done_late':
      return <svg {...p}><path d="M2 8.6l2.6 2.6 5.2-5.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12.2" cy="11.6" r="3.2" fill="var(--log-card)" stroke="currentColor" strokeWidth="1.2" /><path d="M12.2 10.1v1.6l1 0.8" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" /></svg>
    case 'part_done':
      return <svg {...p}><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M8 1.4a6.6 6.6 0 0 1 0 13.2z" fill="currentColor" /></svg>
    case 'not_done':
      return <svg {...p}><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
    case 'carried':
      return <svg {...p}><path d="M3 11.5c0-4 2.8-6.5 7-6.5h2.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /><path d="M10.6 2.6L13 5l-2.4 2.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
    case 'set_aside':
      return <svg {...p}><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M5 8h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
    case 'due':
      return <svg {...p}><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>
    // ticked by the member, before the review: an outlined circle with a tick
    case 'ticked':
      return <svg {...p}><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M4.9 8.3l2 2 4.2-4.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
    // where someone stands, and a month's mark
    case 'on':
    case 'on_track':
      return <svg {...p}><circle cx="8" cy="8" r="5" fill="currentColor" /></svg>
    case 'off':
    case 'off_track':
      return <svg {...p}><circle cx="8" cy="8" r="5.4" fill="none" stroke="currentColor" strokeWidth="2.2" /></svg>
    case 'paused':
      return <svg {...p}><path d="M6 4.5v7M10 4.5v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    case 'in_progress':
      return <svg {...p}><circle cx="8" cy="8" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeDasharray="2.6 2" /></svg>
    case 'no_actions':
      return <svg {...p}><path d="M4.5 8h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
    case 'to_come':
      return <svg {...p}><circle cx="8" cy="8" r="2" fill="currentColor" opacity="0.5" /></svg>
    default:
      // getting started and to do: an outline dash
      return <svg {...p}><rect x="3" y="6.4" width="10" height="3.2" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg>
  }
}

// The @page margin boxes: the name and day bottom left, Page 1 of 3 bottom right (Chrome prints them). Given through
// the CSSOM, as a sheet of its own while the log is drawn: the site's Content-Security-Policy (style-src 'self') refuses
// an inline <style>, and it doesn't refuse a constructed sheet.
const cssString = s => `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, ' ')}"`
export const printPageRule = foot => `@page { size: A4; margin: 16mm; @bottom-left { content: ${cssString(foot)}; font-size: 8pt; } @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 8pt; } }`
function PrintPage({ foot }) {
  useEffect(() => {
    if (typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in document)) return undefined
    const sheet = new CSSStyleSheet()
    try { sheet.replaceSync(printPageRule(foot)) } catch { return undefined }
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet]
    return () => { document.adoptedStyleSheets = document.adoptedStyleSheets.filter(s => s !== sheet) }
  }, [foot])
  return null
}

function Measure({ m }) {
  return (
    <section className={`mlog-measure mlog-measure--${m.key}${m.mark === 'off' || m.mark === 'overdue' ? ' mlog-measure--off' : ''}`} aria-labelledby={`mlog-m-${m.key}`}>
      <div className="mlog-measure-head">
        <h3 className="mlog-measure-h" id={`mlog-m-${m.key}`}>{m.h}</h3>
        {m.mark && <span className={`mlog-mark mlog-mark--${m.mark}`}><MarkIcon mark={m.mark === 'overdue' ? 'off' : m.mark} />{LOG.mark[m.mark]}</span>}
      </div>
      <p className="mlog-figure">{m.figure}{m.label && <span className="mlog-figure-label"> {m.label}</span>}</p>
      {m.bar && (
        <div className="mlog-bar" aria-hidden="true">
          {m.bar.filter(([, w]) => w > 0).map(([k, w]) => <span key={k} className={`mlog-bar-part mlog-bar-part--${k}`} style={{ width: `${w}%` }} />)}
        </div>
      )}
      <p className="mlog-detail">{m.detail}</p>
      {m.carried && <p className="mlog-detail mlog-detail--carried"><MarkIcon mark="carried" />{m.carried}</p>}
      {m.extra.map(line => <p key={line} className="mlog-detail mlog-detail--extra">{line}</p>)}
    </section>
  )
}

// A month's session line, its "recording not watched" a link to the recording on screen where there is 1 (member 9),
// opened in the app without a reload
function MonthLine({ line, sessionId, recordingHref }) {
  const href = sessionId && recordingHref ? recordingHref(sessionId) : null
  const at = href ? line.lastIndexOf(LOG_MEMBER.notWatched) : -1
  if (at < 0) return <p className="mlog-month-line">{line}</p>
  return (
    <p className="mlog-month-line">
      {line.slice(0, at)}
      <Link className="mlog-watch" to={href}>{LOG_MEMBER.notWatched}</Link>
      {line.slice(at + LOG_MEMBER.notWatched.length)}
    </p>
  )
}

// folds: on the member's page, a month that went cleanly folds to its heading on a phone (onFold), and its
// button opens it again; this month is current, drawn above the strip there
function Month({ m, recordingHref = null, folds = false, folded = false, onFold = null, current = false }) {
  const cls = ['mlog-month', `mlog-month--${m.mark}`, current ? 'mlog-month--current' : '', folds && folded ? 'mlog-month--folded' : ''].filter(Boolean).join(' ')
  return (
    <section className={cls} aria-labelledby={`mlog-month-${m.id}`} data-month={m.id}>
      <div className="mlog-month-head">
        <h3 className="mlog-month-h" id={`mlog-month-${m.id}`}>{m.title}</h3>
        <span className={`mlog-mark mlog-mark--${m.mark}`}><MarkIcon mark={m.mark} />{m.word}</span>
        {folds && (
          <button type="button" className="mlog-fold mlog-noprint" aria-expanded={!folded} aria-label={folded ? LOG_PAGE.showMonth(m.month) : LOG_PAGE.hideMonth(m.month)}
            onClick={() => onFold(m.id)}>{folded ? LOG_PAGE.show : LOG_PAGE.hide}</button>
        )}
      </div>
      {m.off.map(line => <p key={line} className="mlog-month-off">{line}</p>)}
      {m.lines.map((line, i) => <MonthLine key={line} line={line} sessionId={m.missed?.[i]} recordingHref={recordingHref} />)}
      {m.review && <p className="mlog-month-line mlog-month-review">{m.review}</p>}
      {m.none
        ? <p className="mlog-month-none">{m.none}</p>
        : (
          <ol className="mlog-actions">
            {m.actions.map(a => (
              <li key={a.id} className={`mlog-action mlog-action--${a.mark}`}>
                <span className="mlog-action-mark"><MarkIcon mark={a.mark} /><span className="mlog-action-word">{a.word}</span></span>
                <span className="mlog-action-text">
                  <span className="mlog-action-body">{a.body}</span>
                  <span className="mlog-action-detail">{a.detail}</span>
                  {a.extra.map(line => <span key={line} className="mlog-action-extra">{line}</span>)}
                </span>
              </li>
            ))}
          </ol>
        )}
    </section>
  )
}

// The document. doc: private.accountability_log's answer. look: 'product' or 'shell'. tools: above the
// document on screen, never printed. lead: under the heading on screen, never printed (the member's refocus card); while
// it shows, what it says too (the headline, what's slipped and the push) is left off the screen and kept in print, so
// the verdict is said once (member 3, 20 Oct). asOf: a kept copy's heading ("As it was on 13 Oct 2026 at 9:42pm."). The
// member's page and Adam's tab draw the same log (Adam, 18 Oct): what's done and what isn't, and the attendance, as plain
// facts, with no rule, limit or percentage; the guarantee's rules stay in Masterminds. The member's page only: pay (the
// day an overdue payment keeps everything open until, log.js measures), recordingHref(session id) for a missed
// session's recording, and aboutTools, its print button in About this log on a phone (above). sessions: the product's
// sessions the page has read (the member's own, or Adam's beside his tab), so an open month is reviewed at a session
// under way at the document's moment rather than the 1 after (log.js runningSession); none, and the document's own.
export function AccountabilityLog({ doc, look = 'product', tools = null, lead = null, asOf = null, headingId = 'mlog-h', pay = null, recordingHref = null, aboutTools = null, sessions = null }) {
  const v = useMemo(() => logView(doc, { helpEmail: SITE.helpEmail, pay, sessions }), [doc, pay, sessions])
  const member = look === 'product'
  // the months opened again on a phone (a clean month starts folded there)
  const [opened, setOpened] = useState(() => new Set())
  if (!v) return null
  const toggle = id => setOpened(o => { const n = new Set(o); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const monthProps = m => ({
    recordingHref: member ? recordingHref : null,
    folds: member && m.clean, folded: member && m.clean && !opened.has(m.id), onFold: toggle, current: member && m.open,
  })
  // a box of the strip jumps to its month, opening it if it's folded
  function jump(e, id) {
    const el = document.getElementById(`mlog-month-${id}`)
    if (!el) return
    e.preventDefault()
    setOpened(o => (o.has(id) ? o : new Set(o).add(id)))
    el.scrollIntoView({ block: 'start' })
  }
  const said = lead ? v.stand.said : 0
  const standKept = v.stand.lines.slice(said)
  const [goalM, memberM] = ['goal', 'membership'].map(k => v.measures.find(x => x.key === k))
  // on a phone, the goal and the membership in 1 line under the measures, an overdue payment marked in amber as the
  // laptop's card marks it
  const facts = (goalM || memberM) && (
    <>
      {goalM && `${goalM.h}: ${goalM.figure}.`}
      {goalM && memberM && ' '}
      {memberM && (
        <>
          {`${memberM.h}: `}
          {memberM.mark === 'overdue' ? <strong className="mlog-facts-off"><MarkIcon mark="off" size={14} />{memberM.figure}</strong> : memberM.figure}
          {`.${memberM.mark !== 'on' && memberM.detail ? ` ${memberM.detail}` : ''}`}
        </>
      )}
    </>
  )
  return (
    <article className={`mlog mlog--${look}`} aria-labelledby={headingId}>
      <PrintPage foot={v.printFoot} />
      <header className="mlog-head">
        <span className="mlog-eyebrow">{v.head.eyebrow}</span>
        <h1 className="mlog-h1" id={headingId}>{v.head.title}</h1>
        <p className="mlog-who">{v.head.line}</p>
        {asOf ? <p className="mlog-asof">{asOf}</p> : v.head.upToDate && <p className="mlog-asof">{v.head.upToDate}</p>}
        {tools && <div className="mlog-tools">{tools}</div>}
      </header>

      {lead && <div className="mlog-lead mlog-noprint">{lead}</div>}

      <section className={`mlog-stand mlog-stand--${v.stand.state}${v.stand.behind ? ' mlog-stand--behind-any' : ''}${said && !standKept.length ? ' mlog-printonly' : ''}`} aria-labelledby="mlog-stand-h">
        <h2 className="mlog-visually-hidden" id="mlog-stand-h">{LOG.standH}</h2>
        <p className={said ? 'mlog-headline mlog-printonly' : 'mlog-headline'}>
          <MarkIcon mark={v.stand.state === 'getting_started' ? 'todo' : v.stand.behind ? 'off' : 'on'} size={22} />
          <span>{v.stand.headline}</span>
        </p>
        {v.stand.lines.map((line, i) => <p key={line} className={i < said ? 'mlog-stand-line mlog-printonly' : 'mlog-stand-line'}>{line}</p>)}
      </section>

      <section className="mlog-measures" aria-labelledby="mlog-measures-h">
        <h2 className="mlog-visually-hidden" id="mlog-measures-h">{LOG.measuresH}</h2>
        <div className="mlog-measure-grid">{v.measures.map(m => <Measure key={m.key} m={m} />)}</div>
        {member && facts && <p className="mlog-facts">{facts}</p>}
      </section>

      <section className="mlog-strip-wrap" aria-labelledby="mlog-strip-h">
        <h2 className="mlog-h2" id="mlog-strip-h">{v.stripTitle}</h2>
        <ol className="mlog-strip">
          {v.strip.map(s => (
            <li key={s.n} className={`mlog-strip-cell mlog-strip-cell--${s.mark}`} aria-label={member && s.id ? undefined : s.aria}>
              {member && s.id
                ? (
                  <a className="mlog-strip-jump" href={`#mlog-month-${s.id}`} aria-label={s.aria} onClick={e => jump(e, s.id)}>
                    <MarkIcon mark={s.mark} /><span className="mlog-strip-n" aria-hidden="true">{s.n}</span>
                  </a>
                )
                : <><MarkIcon mark={s.mark} /><span className="mlog-strip-n" aria-hidden="true">{s.n}</span></>}
            </li>
          ))}
        </ol>
        <ul className="mlog-legend" aria-label="Key">
          {LOG.legend.map(k => <li key={k} className="mlog-legend-item"><MarkIcon mark={k} />{LOG.stripMark[k]}</li>)}
        </ul>
      </section>

      <section className="mlog-months" aria-labelledby="mlog-months-h">
        <h2 className="mlog-h2" id="mlog-months-h">{LOG.monthsH}</h2>
        {v.empty && <p className="mlog-empty">{v.empty}</p>}
        {v.copied && <p className="mlog-copied">{v.copied}</p>}
        {v.months.map(m => <Month key={m.id} m={m} {...monthProps(m)} />)}
        {v.before && (
          <section className="mlog-month mlog-month--before" aria-labelledby="mlog-before-h">
            <div className="mlog-month-head"><h3 className="mlog-month-h" id="mlog-before-h">{v.before.title}</h3></div>
            {v.before.lines.map((line, i) => (
              <MonthLine key={`${i}-${line}`} line={line} sessionId={v.before.missed?.[i]} recordingHref={member ? recordingHref : null} />
            ))}
          </section>
        )}
        {v.after && (
          <div className="mlog-after">
            <h3 className="mlog-h3">{v.after.title}</h3>
            <p className="mlog-month-line">{v.after.line}</p>
            {v.after.months.map(m => <Month key={m.id} m={m} {...monthProps(m)} current={false} />)}
          </div>
        )}
      </section>

      <section className="mlog-wins" aria-labelledby="mlog-wins-h">
        <h2 className="mlog-h2" id="mlog-wins-h">{LOG.winsH}</h2>
        <p className="mlog-month-line">{v.wins.count}</p>
        {v.wins.items.length > 0 && (
          <ul className="mlog-win-list">
            {v.wins.items.map(w => (
              <li key={w.id} className="mlog-win">
                <span className="mlog-win-title">{w.title}</span> <span className="mlog-win-line">{w.line}</span>
                {w.detail && (
                  <details className="mlog-win-more">
                    <summary>{LOG.winMore}</summary>
                    <p>{w.detail}</p>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`mlog-rules${v.rules.member ? ' mlog-rules--member' : ''}`} aria-labelledby="mlog-rules-h">
        <h2 className="mlog-h2" id="mlog-rules-h">{v.rules.h}</h2>
        {v.rules.intro && <p className="mlog-rules-intro">{v.rules.intro}</p>}
        {v.rules.items.length > 0 && <ul className="mlog-rules-list">{v.rules.items.map(r => <li key={r}>{r}</li>)}</ul>}
        <p className="mlog-rules-foot">{v.rules.foot}</p>
        {v.rules.kept && <p className="mlog-rules-foot">{v.rules.kept}</p>}
        {aboutTools && <div className="mlog-about-tools mlog-noprint">{aboutTools}</div>}
      </section>
    </article>
  )
}
